import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ConfigProvider, Flex } from "antd";
import { CheckCircleFilled, EditOutlined, FileTextOutlined } from "@ant-design/icons";
import { SbTag, SbButton, SbTooltip } from "uiCore";
import { useMatchThresholds } from "Store/useTalentMobilitySettingsStore";
import { getScoreColor } from "Utils/scoreColor.utils";
import {
  getFilters,
  toFilterOptions,
  buildPersonColumns,
  buildScoreColumn,
  buildShortlistReasonColumn,
} from "../RedeploymentCenterV2/RedeploymentCenterV2.utils";

// PAIC Redesign Plan tab's plans list: the plan's Org Design version name
// (GET /view-planning/paic-redesign-plans project_name). Module-level so
// PlansListTable's columns memo stays stable.
export const PAIC_PLAN_EXTRA_COLUMNS = [
  {
    title: "Project Name",
    dataIndex: "project_name",
    key: "project_name",
    render: (v) => v || "—",
  },
];

// The Created By cell shows created_by_name with created_by as fallback, so
// the filter (SbTable's auto filter, via filterValue) works on the same
// effective value.
const createdByOf = (record) => record.created_by_name || record.created_by || "";

// GET /view-planning/redeployment-plans and GET /view-planning/workforce-plans
// share the record shape — one row per saved plan, with job_length counted
// from the plan's detail rows in BigQuery — so both tabs share these columns.
// Takes the loaded rows to build the filter option lists from. onEdit adds
// the trailing Edit column (each tab passes its own handler, opening its
// planning page with the plan loaded). extraColumns are inserted right after
// Plan Name — the PAIC Redesign Plan tab adds its Project Name there.
// onExport, when passed, adds an Export CSV action beside Edit (PAIC tab);
// exportingPlanIds lists the rows whose export is in progress.
const PLAN_COUNT_KEY = "job_length";
// Edit / Export icons: antd sizes an icon-only small button's icon with
// onlyIconSizeSM (from the 13px base text) — raised here through the theme
// token, so the icon grows while the compact small button keeps its size.
const PLAN_ACTION_ICON_THEME = { components: { Button: { onlyIconSizeSM: 18 } } };

export const buildPlanColumns = (rows, onEdit, extraColumns = [], { onExport, exportingPlanIds = [] } = {}) => {
  const columns = [
    {
      title: "Plan Name",
      dataIndex: "plan_name",
      key: "plan_name",
      render: (v) => v || "—",
    },
    ...extraColumns,
    {
      title: "Created By",
      dataIndex: "created_by_name",
      key: "created_by_name",
      filterValue: createdByOf,
      render: (name, record) => name || record.created_by || "—",
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
      render: (createdAt) => (createdAt ? new Date(createdAt).toLocaleDateString() : "—"),
    },
    {
      title: "Jobs",
      dataIndex: PLAN_COUNT_KEY,
      key: PLAN_COUNT_KEY,
      sorter: (a, b) => (a[PLAN_COUNT_KEY] ?? 0) - (b[PLAN_COUNT_KEY] ?? 0),
      // Filter on the 0 the cell shows for a missing count, not "(Blank)".
      filterValue: (record) => record[PLAN_COUNT_KEY] ?? 0,
      render: (v) => v ?? 0,
    },
  ];

  if (onEdit || onExport) {
    columns.push({
      title: "",
      key: "edit",
      align: "center",
      width: onEdit && onExport ? 96 : 60,
      render: (_, record) => (
        <ConfigProvider theme={PLAN_ACTION_ICON_THEME}>
          <Flex justify="center" gap={4}>
            {onEdit && (
              <SbButton
                buttonType="action"
                size="small"
                icon={<EditOutlined />}
                title="Edit this plan"
                aria-label="Edit this plan"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(record);
                }}
              />
            )}
            {onExport && (
              <SbButton
                buttonType="action"
                size="small"
                icon={<FileTextOutlined />}
                title="Export CSV"
                aria-label="Export CSV"
                loading={exportingPlanIds.includes(record.plan_id)}
                onClick={(e) => {
                  e.stopPropagation();
                  onExport(record);
                }}
              />
            )}
          </Flex>
        </ConfigProvider>
      ),
    });
  }

  return columns;
};

// The title cell shows the JD title with jd_sb_unique_id as fallback, so the
// filter matches on the same effective value.
const titleOf = (record) => record.title || record.jd_sb_unique_id;

// Shared drill-down column pieces — both plan tables show the same JD fields
// (title + the four org columns) and the two position counts.
const buildTitleColumn = (label, rows) => ({
  title: label,
  dataIndex: "title",
  key: "title",
  filters: toFilterOptions(rows.map(titleOf)),
  filterSearch: true,
  onFilter: (value, record) => titleOf(record) === value,
  render: (title, record) => title || record.jd_sb_unique_id || "—",
});

const buildJdFilterColumns = (rows) =>
  [
    { title: "Business Unit", dataIndex: "businessUnit" },
    { title: "Function", dataIndex: "function" },
    { title: "Sub-function", dataIndex: "subFunction" },
    { title: "Location", dataIndex: "location" },
  ].map(({ title, dataIndex }) => ({
    title,
    dataIndex,
    key: dataIndex,
    filters: getFilters(rows, dataIndex),
    filterSearch: true,
    onFilter: (value, record) => record[dataIndex] === value,
    render: (v) => v || "—",
  }));

// A centered count column (dash for a missing count); SbTable auto-filters it.
const buildCountColumn = (title, dataIndex) => ({
  title,
  dataIndex,
  key: dataIndex,
  align: "center",
  // align: "center" only centers the body cells — a sortable or filterable
  // header's title+icon lays out in antd's own flex box, untouched by align.
  onHeaderCell: () => ({ className: "tm-col-header-center" }),
  render: (v) => v ?? "—",
});

const withCountSorter = (column) => ({
  ...column,
  sorter: (a, b) => (a[column.dataIndex] ?? -1) - (b[column.dataIndex] ?? -1),
});

const POSITION_COLUMNS = [
  withCountSorter(buildCountColumn("Current Positions", "current_position")),
  withCountSorter(buildCountColumn("Target Positions", "target_position")),
];

// GET /view-planning/redeployment-plan/roles records — the /talent-mobility/employees
// record shape plus plan fields, so the columns mirror the manager table's
// vocabulary (position counts). Takes the loaded rows to build the filter
// option lists from.
export const buildRedeploymentRoleColumns = (rows) => [
  buildTitleColumn("Role", rows),
  ...buildJdFilterColumns(rows),
  ...POSITION_COLUMNS,
];

// GET /view-planning/paic-redesign-plan/roles records, one role tab's rows —
// Role, Before HC, After HC, and how many employees the plan shortlisted for
// the role (a reduced role's people made available, or an added role's
// candidates), which is what clicking a row expands into.
export const buildPaicRoleColumns = (rows) => [
  buildTitleColumn("Role", rows),
  buildCountColumn("Before HC", "before_hc"),
  buildCountColumn("After HC", "after_hc"),
  buildCountColumn("Shortlisted", "shortlisted_count"),
];

// GET /view-planning/workforce-plan/jobs records — same JD columns as the
// redeployment roles table, plus how many people the saved plan shortlisted
// for the job.
export const buildWorkforceJobColumns = (rows) => [
  buildTitleColumn("Job", rows),
  ...buildJdFilterColumns(rows),
  ...POSITION_COLUMNS,
  {
    title: "Shortlisted",
    dataIndex: "shortlisted_count",
    key: "shortlisted_count",
    align: "center",
    sorter: (a, b) => (a.shortlisted_count ?? 0) - (b.shortlisted_count ?? 0),
    filterValue: (record) => record.shortlisted_count ?? 0,
    // align: "center" only centers the body cells — a sortable header's
    // title+icon lays out in antd's own flex box, untouched by align.
    onHeaderCell: () => ({ className: "tm-col-header-center" }),
    render: (v) => v ?? 0,
  },
];

// Role drill-down columns — same split as useRedeploymentCenterColumns: the
// component owns data and state, this hook owns presentation. "#" and Status
// both render from record.rank (the person's position in the full SMI
// ranking), so pagination, the SMI sorter, and filters never renumber
// anyone or shift their Retained/At Risk status. Every visible row is
// marked available by the saved plan.
export const usePlanRoleDetailColumns = (cutoff, rows, shortlistReasonByEmployee) => {
  const thresholds = useMatchThresholds();

  return useMemo(() => {
    // One status rule for the Status tag, its filter and the Reason
    // placeholder, so the three can never disagree on a row.
    const statusOf = (record) => (record.rank >= cutoff ? "At Risk" : "Retained");
    const isRetained = (record) => statusOf(record) === "Retained";

    return [
      { title: "#", key: "rank", width: 50, render: (_, record) => record.rank + 1 },
      ...buildPersonColumns(rows),
      buildScoreColumn("SRI (this role)", "matchPct", thresholds, {
        index: "SRI",
        // align: "center" (buildScoreColumn's default) only centers the body
        // cells — the sortable header's title+icon group needs this class too.
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
      }),
      {
        title: "Status",
        key: "status",
        align: "center",
        filters: [
          { text: "Retained", value: "Retained" },
          { text: "At Risk", value: "At Risk" },
        ],
        filterSearch: true,
        onFilter: (value, record) => statusOf(record) === value,
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
        render: (_, record) =>
          statusOf(record) === "At Risk" ? (
            <SbTag color="error">At Risk</SbTag>
          ) : (
            <SbTag color="success">Retained</SbTag>
          ),
      },
      {
        // Every listed row is marked available by the saved plan, so the
        // only option is "Yes" — kept for header consistency.
        title: "Available",
        key: "available",
        align: "center",
        filters: [{ text: "Yes", value: true }],
        filterSearch: true,
        onFilter: (value) => value === true,
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
        render: () => <SbTag color="success" icon={<CheckCircleFilled />}>Yes</SbTag>,
      },
      // The Reason for Shortlisting saved with the plan (required on every
      // redeployment shortlist, see ShortlistConfirmModal).
      buildShortlistReasonColumn(shortlistReasonByEmployee, isRetained),
    ];
  }, [thresholds, cutoff, rows, shortlistReasonByEmployee]);
};

// Workforce job drill-down columns — the same ranked-table format as the
// redeployment role drill-down. Every row is a person the saved plan
// shortlisted for the job; Available comes from the people-availability
// endpoint (backend-owned), and View Profile is the read-only
// flow=view-planning Candidate Profile jump.
export const useWorkforceJobCandidateColumns = (jobJdId, rows) => {
  const thresholds = useMatchThresholds();
  const navigate = useNavigate();

  return useMemo(() => {
    // "#" is each person's fixed position in the SMI ranking (rows arrive
    // sorted by SMI) — a per-page row index would restart at 1 on page 2
    // and renumber under the SMI sorter.
    const rankByPersonId = new Map(rows.map((row, index) => [row.personId, index]));
    return [
      { title: "#", key: "rank", width: 50, render: (_, record) => rankByPersonId.get(record.personId) + 1 },
      ...buildPersonColumns(rows),
      buildScoreColumn("SMI (this job)", "sri", thresholds, {
        // Overrides the default render — null (not yet scored) would
        // otherwise print "null%"; same dash + tooltip convention as
        // RoleDetailV2's own SMI column and the Next Roles panels.
        render: (sri) =>
          sri == null ? (
            <SbTooltip content="User has not updated profile">—</SbTooltip>
          ) : (
            <SbTag color={getScoreColor(sri, thresholds)}>{sri}%</SbTag>
          ),
        // align: "center" (buildScoreColumn's default) only centers the body
        // cells — the header's title+sort-icon group needs this class too
        // (see sharedTable.styles.js's .tm-col-header-center).
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
      }),
      {
        // Every listed row is on the plan's shortlist — this column is the
        // confirmation tag, same as the redeployment table's Available "Yes",
        // so the only filter option is "Yes".
        title: "Shortlisted",
        key: "shortlisted",
        align: "center",
        filters: [{ text: "Yes", value: true }],
        filterSearch: true,
        onFilter: (value) => value === true,
        render: () => <SbTag color="success" icon={<CheckCircleFilled />}>Yes</SbTag>,
        // Filterable columns default to flex-start (sharedTable.styles.js,
        // for short-label funnel-icon alignment app-wide) — center this one
        // specifically, same opt-in class the SMI column above uses.
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
      },
      {
        title: "",
        key: "actions",
        align: "center",
        render: (_, record) => (
          <SbButton
            buttonType="secondary"
            size="small"
            onClick={() =>
              navigate(`/talent-mobility/candidate/${record.personId}/role/${jobJdId}?index=smi&flow=view-planning`)
            }
          >
            View Profile
          </SbButton>
        ),
      },
    ];
  }, [thresholds, navigate, jobJdId, rows]);
};
